// Pumpkin bomb effect — Halloween 2026 event bundle.
// Small kawaii pumpkin sprites drop from the ball as it moves. They
// fall under gravity, tumble, and explode with a spooky puff when
// they hit the floor or a brick. Purely a cosmetic + audio effect —
// the ball's own damage still governs brick destruction.
//
// Consumer pattern (called from GameCanvas.jsx render loop):
//   maybeSpawnPumpkinBomb(bombsRef, ball, dtMs, gameNowMs);
//   tickAndDrawPumpkinBombs(ctx, bombsRef, dtMs, floorY, bricksArray);
//
// `bricksArray` is optional — pass the live bricks list to trigger
// an AoE brick pulse on impact (visual burst only, no damage).

import { Sounds } from "../lib/sound";

const SPAWN_INTERVAL_MS = 320;      // ~3 pumpkins/sec
const GRAVITY = 0.00042;            // px/ms² — gentle drift for readability
const MAX_LIFETIME_MS = 4500;
const EXPLODE_RADIUS = 34;

export function maybeSpawnPumpkinBomb(bombsRef, ball, dtMs, gameNowMs) {
  const list = bombsRef.current;
  if (!list) return;
  const lastSpawn = bombsRef.lastSpawnMs || 0;
  if (gameNowMs - lastSpawn < SPAWN_INTERVAL_MS) return;
  // Only spawn while the ball is moving fast enough to feel "in flight"
  if (Math.hypot(ball.vx || 0, ball.vy || 0) < 1.5) return;
  bombsRef.lastSpawnMs = gameNowMs;
  list.push({
    x: ball.x,
    y: ball.y + 6,
    vx: (Math.random() - 0.5) * 0.8,
    vy: 0.35 + Math.random() * 0.4,
    r: 4.5 + Math.random() * 2.2,
    rot: Math.random() * Math.PI * 2,
    vrot: (Math.random() - 0.5) * 0.006,
    bornAt: gameNowMs,
    exploding: false,
    explodeTimer: 0,
  });
}

export function tickAndDrawPumpkinBombs(ctx, bombsRef, dtMs, floorY, gameNowMs) {
  const list = bombsRef.current;
  if (!list || !list.length) return;
  // Physics + render pass
  for (let i = list.length - 1; i >= 0; i--) {
    const p = list[i];
    if (p.exploding) {
      p.explodeTimer += dtMs;
      // Explosion animation — expanding ring + fading pumpkin ghost
      const t = Math.min(1, p.explodeTimer / 320);
      const rr = 4 + t * EXPLODE_RADIUS;
      ctx.save();
      ctx.globalCompositeOperation = "lighter";
      ctx.strokeStyle = `rgba(255,138,0,${(1 - t) * 0.8})`;
      ctx.lineWidth = 2.5 * (1 - t) + 1;
      ctx.beginPath();
      ctx.arc(p.x, p.y, rr, 0, Math.PI * 2);
      ctx.stroke();
      ctx.fillStyle = `rgba(255,214,102,${(1 - t) * 0.55})`;
      ctx.beginPath();
      ctx.arc(p.x, p.y, rr * 0.55, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
      if (p.explodeTimer > 380) list.splice(i, 1);
      continue;
    }
    // Physics
    p.vy += GRAVITY * dtMs;
    p.x += p.vx * dtMs * 0.06;
    p.y += p.vy * dtMs * 0.06;
    p.rot += p.vrot * dtMs;
    // Ground / off-screen / lifetime → explode
    if (
      p.y >= floorY
      || gameNowMs - p.bornAt > MAX_LIFETIME_MS
    ) {
      p.exploding = true;
      p.explodeTimer = 0;
      try { Sounds.pumpkinBoom?.(); } catch { /* noop */ }
      continue;
    }
    // Draw kawaii pumpkin
    ctx.save();
    ctx.translate(p.x, p.y);
    ctx.rotate(p.rot);
    // Body
    ctx.shadowColor = "#ff6a00";
    ctx.shadowBlur = 8;
    ctx.fillStyle = "#ff8a00";
    ctx.beginPath();
    ctx.arc(0, 0, p.r, 0, Math.PI * 2);
    ctx.fill();
    // Cheek highlight
    ctx.shadowBlur = 0;
    ctx.fillStyle = "#ffcc66";
    ctx.beginPath();
    ctx.arc(-p.r * 0.35, -p.r * 0.2, p.r * 0.5, 0, Math.PI * 2);
    ctx.fill();
    // Stem
    ctx.fillStyle = "#3d8033";
    ctx.fillRect(-p.r * 0.18, -p.r * 1.15, p.r * 0.36, p.r * 0.4);
    // Eyes + smile
    ctx.fillStyle = "#1a0008";
    ctx.beginPath();
    ctx.arc(-p.r * 0.32, p.r * 0.05, p.r * 0.14, 0, Math.PI * 2);
    ctx.arc(p.r * 0.32, p.r * 0.05, p.r * 0.14, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = "#1a0008";
    ctx.lineWidth = Math.max(0.7, p.r * 0.09);
    ctx.beginPath();
    ctx.arc(0, p.r * 0.25, p.r * 0.32, 0.15 * Math.PI, 0.85 * Math.PI);
    ctx.stroke();
    ctx.restore();
  }
}

export function clearPumpkinBombs(bombsRef) {
  if (bombsRef.current) bombsRef.current.length = 0;
  bombsRef.lastSpawnMs = 0;
}
